// Tenant detection from the current hostname.
//
// Production URL strategy (committed in #85, see project_brand_name.md):
//   <slug>.<app host>               — every tenant gets a default subdomain
//   awareness.acme.com             — Enterprise add-on (custom domain)
//   <app host>                     — bare apex, no tenant pre-selected
//
// In dev / local you can short-circuit this by setting `VITE_DEV_TENANT_SLUG`
// in `.env.local` so localhost behaves as if it were `<slug>.<app host>`
// without you having to fake the hostname via `/etc/hosts`.
//
// `VITE_APP_HOST_SUFFIX` is the parent domain — defaults to the hostname of
// `VITE_PUBLIC_APP_URL` (MP411)
// but env-overridable so we can run staging on a different parent (e.g.
// `app.staging.x-one.com`) without code changes.

// ---------------------------------------------------------------
// MP738 — standalone-app mode (designed here as a seam in MP700).
//
// X-One Resilience, X-One AI Gateway and X-One Umbra are each ALSO sold standalone,
// at their own domain, to a buyer who must never see the suite (see
// docs/programs/x-one-resilience-app-scoping.md and ADR 0030 decision
// 2). Standalone is NOT a code fork: it is this same React tree with
// brand and navigation chosen from the hostname.
//
// `standaloneAppFromHostname()` below mirrors `tenantFromHostname`:
// a `VITE_DEV_STANDALONE_APP` override so localhost can exercise the
// mode without /etc/hosts (honoured on local hosts only, so a stray
// build variable can never turn a deployed suite standalone), then an
// exact match against `VITE_STANDALONE_HOST_MAP`
// (`resilience.example.com=resilience,gateway.example.com=ai-gateway,umbra.example.com=umbra`),
// then `null` for every ambiguous or invalid case so the suite chrome
// is always the safe fallback. The rules and the per-app registry live
// in lib/standaloneApp.ts (pure, tested).
//
// Consumers — all of them read `currentStandaloneBrand()`, so
// standalone mode never becomes a second code path:
//   - AppLayout: no app switcher, Apps panel, launcher link or
//     cross-app menus / palette entries / Go-to keys; product branding.
//   - LauncherPage and the `/` redirect: straight to the entry route.
//   - LoginPage / SignupPage: product-branded copy.
//   - StandaloneRouteGuard (App.tsx): other apps' routes and the
//     cross-app organization pages redirect to the entry route.
//   - applyStandaloneDocumentBranding (index.tsx): tab title + icon.
//
// Server side: a standalone tenant is an ordinary X-One tenant flagged
// `standaloneApp: '<app>'` on its org doc (MP486,
// packages/shared-types/src/standaloneApp.ts), which makes its app
// entitlements that one app only. Same Firestore, same GCIP, same api —
// which is what makes MP743's standalone → suite upgrade a
// zero-migration flip (clear the flag in the platform console). Links the api builds (emails,
// webhooks, OAuth returns) still use PUBLIC_APP_URL; per-tenant
// standalone links are a follow-up.
// ---------------------------------------------------------------

// MP411 — the parent domain tenant subdomains hang off. Defaults to the
// hostname of PUBLIC_APP_URL (i.e. whatever the app is actually served
// from), so picking the beta domain is a variables change rather than an
// edit here. `VITE_APP_HOST_SUFFIX` still overrides it, which is how a
// local dev session pretends to be `<slug>.<suffix>`.

import { PUBLIC_HOSTS } from './publicHosts';
import { deriveTenantHint, type TenantHostHint } from './tenantHostRules';
import {
  deriveStandaloneHint,
  standaloneBrand,
  standaloneFaviconDataUri,
  type StandaloneBrand,
  type StandaloneHostHint,
} from './standaloneApp';

export type { TenantHostHint, StandaloneHostHint, StandaloneBrand };

const DEFAULT_HOST_SUFFIX = PUBLIC_HOSTS.appHost;

let warnedMisconfiguration = false;

/** Extract a tenant slug from window.location (or a provided hostname).
 *  Returns null in all the ambiguous cases — bare apex, the app's own
 *  host, IP addresses, unknown parent domains, anything not matching the
 *  slug shape — so the UI can fall back to manual entry without trying
 *  to be clever. The rule itself lives in tenantHostRules.ts (pure,
 *  tested); this wrapper only reads the environment. */
export function tenantFromHostname(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
): TenantHostHint {
  // Dev shortcut. Honored even when the hostname looks like a real
  // production URL — assumed deliberate (e.g. running a local build
  // against a staging Cloud Run with the prod hostname).
  const envOverride = import.meta.env.VITE_DEV_TENANT_SLUG as string | undefined;
  const suffix =
    (import.meta.env.VITE_APP_HOST_SUFFIX as string | undefined)?.trim() ||
    DEFAULT_HOST_SUFFIX;

  const hint = deriveTenantHint(hostname, suffix, PUBLIC_HOSTS.appHost, envOverride);
  if (hint.misconfiguration && !warnedMisconfiguration) {
    warnedMisconfiguration = true;
    console.warn(`[tenant-host] ${hint.misconfiguration}`);
  }
  return hint;
}

let warnedStandalone = false;

/** Which standalone product this hostname serves, if any. Null (the
 *  suite) in every ambiguous case; configuration problems go to one
 *  console warning. The rule lives in standaloneApp.ts (pure, tested);
 *  this wrapper only reads the environment. */
export function standaloneAppFromHostname(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
): StandaloneHostHint {
  const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const hint = deriveStandaloneHint(
    hostname,
    env.VITE_STANDALONE_HOST_MAP,
    PUBLIC_HOSTS.appHost,
    env.VITE_DEV_STANDALONE_APP,
  );
  if (hint.issues?.length && !warnedStandalone) {
    warnedStandalone = true;
    console.warn(`[standalone-host] ${hint.issues.join('; ')}`);
  }
  return hint;
}

let cachedBrand: StandaloneBrand | null | undefined;

/** The standalone product for this page load, or null on the suite.
 *  The hostname cannot change without a reload, so it is resolved once. */
export function currentStandaloneBrand(): StandaloneBrand | null {
  if (cachedBrand === undefined) {
    const { app } = standaloneAppFromHostname();
    cachedBrand = app ? standaloneBrand(app) : null;
  }
  return cachedBrand;
}

/** On a standalone host the browser tab shows the product's name and
 *  icon before any page renders (the sign-in page has no AppLayout). */
export function applyStandaloneDocumentBranding(): void {
  const brand = currentStandaloneBrand();
  if (!brand || typeof document === 'undefined') return;
  document.title = brand.productName;
  const href = standaloneFaviconDataUri(brand);
  const links = document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']");
  if (links.length === 0) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
    return;
  }
  links.forEach((l) => {
    l.href = href;
    l.removeAttribute('type');
  });
}
